import { CommonModule } from "@angular/common";
import { Component, type OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { Store } from "@ngrx/store";
import { AccordionModule } from "primeng/accordion";
import { Subscription, firstValueFrom } from "rxjs";
import { ButtonDirective } from "src/app/core/directives";
import { toFormData } from "src/app/shared/helpers";
import { ExamInterface, ResultPreviewDataInterface, ResultUploadUrlResponseInterface } from "src/app/shared/interfaces";
import { ResultService } from "src/app/shared/services/result";
import {
    examActions,
    resultActions,
    selectExams,
    selectIsMutationLoading,
    selectPreviewData,
    selectUploadUrlData,
} from "src/app/shared/store";
import { AccordionTabComponent, AlertComponent, AutocompleteComponent } from "../../ui";
import { FileInputComponent } from "../../ui/inputs";

@Component({
    selector: "sms-result-form",
    standalone: true,
    imports: [
        CommonModule,
        FileInputComponent,
        ButtonDirective,
        ReactiveFormsModule,
        RouterLink,
        AccordionModule,
        AccordionTabComponent,
        AutocompleteComponent,
        AlertComponent,
    ],
    templateUrl: "./result-form.component.html",
    styleUrls: ["./result-form.component.scss"],
})
export class ResultFormComponent implements OnInit {
    formGroup: FormGroup;
    isLoading$ = this.store.select(selectIsMutationLoading);
    classroomRef!: string;
    previewData: ResultPreviewDataInterface | null;
    subscription = new Subscription();
    examsOptions: ExamInterface[];

    // Upload progress tracking
    isUploading = false;
    currentUploadFile = "";
    currentFileIndex = 0;
    totalFiles = 0;
    uploadProgress = "";

    constructor(
        private formBuilder: FormBuilder,
        public route: ActivatedRoute,
        private store: Store,
        private resultService: ResultService,
    ) {
        this.formGroup = this.formBuilder.group({
            exam: new FormControl("", Validators.required),
            // title: new FormControl("", Validators.required),
            // description: new FormControl(""),
            attachments: new FormControl([]),
        });
    }

    ngOnInit(): void {
        this.store.dispatch(resultActions.clearData());

        if (this.route.snapshot.queryParams["classroomRef"]) {
            this.classroomRef = this.route.snapshot.queryParams["classroomRef"];
        }

        this.store.select(selectPreviewData).subscribe((resPreviewData) => {
            this.previewData = resPreviewData;
        });

        this.store.select(selectUploadUrlData).subscribe({
            next: (fileUploadUrlsData) => {
                if (fileUploadUrlsData) this.uploadFilesToServer(fileUploadUrlsData);
            },
        });

        this.store.select(selectExams).subscribe((exams) => {
            this.examsOptions = exams;
        });

        this.store.dispatch(examActions.loadExams());
    }

    onPreview() {
        this.formGroup.markAllAsTouched();
        if (this.formGroup.valid) {
            let data: any = {
                classroom_id: this.classroomRef,
            };

            let filenames = this.formGroup
                .getRawValue()
                ?.attachments?.map((item: File) => item.name?.replace(".pdf", ""));
            data["form_numbers"] = filenames;

            this.store.dispatch(
                resultActions.preview({
                    data: data,
                }),
            );
        }
    }

    onGenerateUrl() {
        let confirmUploadFiles = this.previewData?.not_found_admissions?.length ? false : true;
        if (this.previewData?.not_found_admissions?.length) {
            confirmUploadFiles = window.confirm(
                `Some file names are not matching with the students in the system. Only matching files will be uploaded. Are you sure you want to upload ?`,
            );
        }

        if (!confirmUploadFiles) {
            return;
        }

        const filesToUpload: { filename: string; admission_id: string }[] = [];

        let filenames = this.formGroup.getRawValue()?.attachments?.map((item: File) => item.name?.replace(".pdf", ""));
        this.previewData?.found_admissions?.forEach((adm) => {
            const file = filenames.find((name: string) => name == adm.form_number);

            if (file) {
                filesToUpload.push({
                    filename: `${file}.pdf`,
                    admission_id: adm.admission_id,
                });
            }
        });

        this.store.dispatch(
            resultActions.generateUploadUrl({
                data: {
                    exam_id: this.formGroup.getRawValue()?.exam?.id,
                    files: filesToUpload,
                },
            }),
        );
    }

    async uploadFilesToServer(data: ResultUploadUrlResponseInterface[]) {
        let files = this.formGroup.getRawValue()?.attachments;
        const confirmPayload: { admission_id: string; file_key: string }[] = [];

        // Initialize upload progress
        this.isUploading = true;
        this.totalFiles = this.previewData?.found_admissions?.length || 0;
        this.currentFileIndex = 0;

        let filteredFiles = files?.filter((f: File) => data.some((i) => i.filename === f.name));

        try {
            // Upload each file with progress tracking
            for (let i = 0; i < filteredFiles.length; i++) {
                const file = filteredFiles[i];
                this.currentFileIndex = i + 1;
                this.currentUploadFile = file.name;
                this.uploadProgress = `${this.currentFileIndex}/${this.totalFiles}`;

                const fileInfo = data.find((r: any) => r.filename === file.name);
                if (fileInfo) {
                    await firstValueFrom(this.resultService.uploadToPresignedUrl(fileInfo.upload_url, file));
                    confirmPayload.push({
                        admission_id: fileInfo.admission_id,
                        file_key: fileInfo.file_key,
                    });
                }
            }

            if (confirmPayload.length) {
                this.store.dispatch(
                    resultActions.confirmResultUpload({
                        data: {
                            exam_id: this.formGroup.getRawValue()?.exam?.id,
                            classroom_id: this.classroomRef,
                            files: confirmPayload,
                        },
                    }),
                );
            }
        } finally {
            // Reset upload progress state
            this.isUploading = false;
            this.currentUploadFile = "";
            this.currentFileIndex = 0;
            this.totalFiles = 0;
            this.uploadProgress = "";
        }
    }

    onSubmit() {
        this.formGroup.markAllAsTouched();
        if (this.formGroup.valid) {
            let data = {
                ...this.formGroup.getRawValue(),
            };

            this.store.dispatch(
                resultActions.create({
                    data: toFormData(data, ["attachments"]),
                }),
            );
        }
    }

    goToForm() {
        this.store.dispatch(resultActions.clearData());
    }
}
